/**
 * 导航适配器（NavigationAdapter）——"把目的地交给设备上的地图能力"的唯一分发层
 *
 * 三平台策略（最小可靠方案：不做 App 检测大全、不加载任何 SDK、不需要任何 Key）：
 *   Android  geo: URI（RFC 5870，平台标准）→ 系统弹应用选择器，装了哪家地图就用哪家
 *   iOS      maps.apple.com（系统地图官方 Web 入口）→ 直接进 Apple Maps
 *   PC/其他  uri.amap.com 官方 URI —— 只是一条普通网页链接（非 JS API、免 Key），
 *            PC 上没有可拉起的地图 App，网页查看位置是唯一合理形态
 *            ★ legacy web navigation：与地图 Provider 无关（不加载 SDK、不消费 Key），
 *            仅因"PC 无厂商中立的位置网页协议"暂留高德链接；待有中立方案再换，勿与
 *            地图 Provider 混为一谈。
 *
 * 坐标系坑位（厂商参数顺序不一致，由本层吸收，调用方无感）：
 *   geo: / maps.apple.com 的 ll 均为 纬度,经度；uri.amap.com 的 position 为 经度,纬度。
 *
 * 坐标系：默认 GCJ02（legacy）——本系统全部历史坐标为 GCJ-02（订单快照 + 后端高德
 * geocode 产出，无第二种来源，审计结论见 map 服务层）。Android 国内地图 App 与
 * Apple 中国区底图（高德供图）均按 GCJ-02 解释坐标，直接传即正确，无需转换。
 *
 * 兜底：地址文本始终由页面层展示（地址为主，本按钮只是增强）；
 *       无地址无坐标 → 返回 ''（调用方不渲染按钮）。
 */

// legacy 默认坐标系：历史数据同源（GCJ-02），adapter 层声明默认值而非迁移 schema
const LEGACY_COORDINATE_SYSTEM = 'GCJ02'

// UA → 平台。一次性字符串判断，不做特性探测、不做 App 安装检测
export function detectPlatform (ua) {
  const s = String(ua || '')
  if (/Android/i.test(s)) return 'android'
  if (/iPhone|iPad|iPod/i.test(s)) return 'ios'
  return 'other'
}

/**
 * 生成导航 URI
 * @param {Object} p
 *   address            地址文本（订单快照，trim 后非空才可用关键字搜索兜底）
 *   latitude/longitude 坐标（Number/String 均可——组件 props 允许 String）
 *   coordinateSystem   默认 GCJ02（legacy）；当前所有来源均 GCJ-02，仅作显式标注
 *   platform           'android' | 'ios' | 'other'；缺省按 navigator.userAgent 探测
 * @returns {string} 可直接放进 <a href> 的 URI；无地址且无坐标返回 ''
 */
export function buildNavigationUri ({ address, latitude, longitude, coordinateSystem = LEGACY_COORDINATE_SYSTEM, platform } = {}) {
  const addr = String(address || '').trim()
  const lat = Number(latitude)
  const lng = Number(longitude)
  // 坐标缺席判定（2026-09-19 修复）：null/undefined/空串绝不是坐标——Number(null)=0、
  // Number('')=0，原先 (0,0) 冒充真坐标生成 marker?position=0,0（青海湖心，导航到湖里）。
  // 行政区级精度的坐标也由调用方（OrderLocationMap 导航门槛）主动不传，走地址检索
  const hasCoord = latitude != null && longitude != null &&
    String(latitude).trim() !== '' && String(longitude).trim() !== '' &&
    !isNaN(lat) && !isNaN(lng)
  if (!addr && !hasCoord) return ''
  const pf = platform || detectPlatform(typeof navigator !== 'undefined' && navigator.userAgent)

  if (pf === 'android') {
    return hasCoord
      ? `geo:${lat},${lng}?q=${encodeURIComponent(addr)}` // 纬度,经度
      : `geo:0,0?q=${encodeURIComponent(addr)}` // 纯搜索（装有的地图 App 自行解析地址）
  }
  if (pf === 'ios') {
    return hasCoord
      ? `https://maps.apple.com/?q=${encodeURIComponent(addr)}&ll=${lat},${lng}` // ll=纬度,经度
      : `https://maps.apple.com/?q=${encodeURIComponent(addr)}`
  }
  return hasCoord
    ? `https://uri.amap.com/marker?position=${encodeURIComponent(lng + ',' + lat)}&name=${encodeURIComponent(addr)}` // position=经度,纬度
    : `https://uri.amap.com/search?keyword=${encodeURIComponent(addr)}`
}
